import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { add, format, parse } from '@fint/money';
import { api, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useBaseCurrency, useFiscalYearStartMonth, useToday } from '@/hooks/useOrgSettings';
import { useIsPhone } from '@/hooks/useIsPhone';
import { currentMonth, financialYearOf, financialYearPeriod, type Period } from '@/lib/dates';
import {
  Card, CardContent, CardHeader, CardTitle, ErrorState, Money, PeriodPicker,
  SkeletonTable, StackedBars, TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';
import { BasisToggle, Signed, Tile, describeBasis, type Basis } from './shared';
import { BreakdownCard } from '@/features/reports/BreakdownCard';
import { MonthsChart } from '@/features/reports/MonthsChart';
import { drillForMonth } from '@/features/reports/drill';

interface MonthRow {
  month: string;
  income: string;
  expense: string;
  /** The realised exchange gain/(loss) on foreign-currency payments that month, folded into profit. Accrual only. */
  exchange?: string;
  profit: string;
  cumulativeProfit: string;
  target: string | null;
  variance: string | null;
}

/**
 * Only what this screen renders.
 *
 * The response carries more - `currency`, `basis`, `fiscalYearStart`,
 * `targetMetric` - and declaring fields nothing reads makes a component look
 * like it depends on them, which is how a field nobody uses survives three
 * refactors. The currency comes from `useBaseCurrency`, which is where every
 * other screen gets it.
 */
interface Overview {
  monthsWithActivity: number;
  months: MonthRow[];
  averages: { income: string; expense: string; profit: string };
  /** The year's exchange gain/(loss); null when no foreign-currency payment landed - then the column is not drawn. */
  exchange?: string | null;
}

interface BreakdownRow {
  categoryId: string | null;
  name: string;
  colour: string;
  /** The category's picture, when it has one. Absent on "Uncategorised". */
  emoji?: string | null;
  amount: string;
  share: number;
  excluded: boolean;
}

interface BreakdownResponse {
  income: BreakdownRow[];
  expense: BreakdownRow[];
  totals: {
    income: string;
    expense: string;
    profit: string;
    target: string | null;
    variance: string | null;
    targetMonths: number;
  };
  excluded: { income: string; expense: string };
  uncategorisedReceipts: { amount: string; count: number };
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (ym: string) => {
  const [year, month] = ym.split('-').map(Number);
  return `${MONTH_NAMES[month - 1]} ${year}`;
};

/**
 * "Money in and out" - the section the owner's spreadsheet is asking for.
 *
 * ── WHY IT SITS BELOW THE EXISTING TILES RATHER THAN REPLACING THEM ──
 * The four tiles above answer "am I owed money", which the spreadsheet does not
 * and which losing would be a regression. This answers "how did the period go".
 *
 * ── THE TILES AND THE CHARTS COME FROM ONE QUERY ──
 * They used to come from two: the charts from the period-scoped breakdown, the
 * tiles from a single ROW of the financial-year series, picked by slicing the
 * first seven characters off the period's start date. That is only right when
 * the period is exactly one month - pick a quarter and the tiles showed April
 * while the charts below them showed all three months, with nothing on screen
 * saying which was which. Now the tile IS the total of the rows underneath it,
 * for any range the picker can produce.
 *
 * The month table and its averages stay on the financial year, deliberately:
 * driving them off the same picker collapses the table to one row the moment
 * somebody picks August, which is the obvious bug. The picked months are marked
 * instead, so the relationship between the two halves is visible.
 *
 * ── WHY THERE ARE NO PIE CHARTS AND NO CHART LIBRARY ──
 * See `RankedBars`: the question is "what is my biggest cost and how much
 * bigger is it than the next one", which is a comparison of lengths.
 */
export function MoneyInAndOut() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const currency = useBaseCurrency();
  const fyStartMonth = useFiscalYearStartMonth();
  const today = useToday();
  const isPhone = useIsPhone();
  const navigate = useNavigate();
  // The table under the chart: open on a laptop, a tap away on a phone,
  // where six rows of five figures lost two columns off the edge.
  const [tableOpen, setTableOpen] = useState(false);

  /**
   * `currentMonth()`, which the Overview page also opens on. It used to be the
   * month of `new Date().toISOString()`, which is UTC's month - at 01:00 IST on
   * the 1st that is still last month, and the two dashboards would have opened
   * on different months for the first five and a half hours of every month.
   * Same fix `todayInTimezone` documents; same failure it prevents.
   */
  const [period, setPeriod] = useState<Period>(() => currentMonth());
  /**
   * "When paid" first. The owner reads the book the way the bank reads it -
   * what has actually come in - and asked for it as the default; "When
   * invoiced" is one tap away for the accountant's view. The API's own
   * default stays accrual: a return is filed on what was invoiced.
   */
  const [basis, setBasis] = useState<Basis>('cash');

  const fy = useMemo(
    () => financialYearPeriod(financialYearOf(period.from, fyStartMonth).startYear, fyStartMonth),
    [period.from, fyStartMonth],
  );

  const orgId = org?.id;
  const enabled = !!orgId && perms.seesAll('report');

  const overview = useQuery({
    queryKey: ['dashboard-overview', orgId, fy.from, basis],
    enabled,
    queryFn: () => api.get<Overview>(`/reports/overview${qs({ fyStart: fy.from, basis })}`),
  });

  const breakdown = useQuery({
    queryKey: ['dashboard-breakdown', orgId, period.from, period.to, basis],
    enabled,
    queryFn: () =>
      api.get<BreakdownResponse>(`/reports/categories${qs({ from: period.from, to: period.to, basis })}`),
  });

  // A report scoped to one person is a different number presented as the
  // business's, so the API refuses it. The section is absent rather than
  // showing zeroes, which would read as "the business made nothing".
  if (!perms.seesAll('report')) return null;

  /**
   * ── THE YEAR SO FAR ──
   * The financial year's months up to the current one. A March that has not
   * happened is not a row of 0.00 and a flat line - it has not happened.
   * The average divides by the months with activity, as it did, and says so.
   */
  const thisMonth = today.slice(0, 7);
  const months = (overview.data?.months ?? []).filter((row) => row.month <= thisMonth);
  const minor = (amount: string) => parse(amount, currency).minor;
  const totals = breakdown.data?.totals;
  // The picked range, as month keys, so the table can mark the rows the tiles
  // are made of - however many that is.
  const pickedMonths = new Set(
    months.filter((row) => row.month >= period.from.slice(0, 7) && row.month <= period.to.slice(0, 7)).map(
      (row) => row.month,
    ),
  );

  /**
   * The goal column is ABSENT when no month of the year carries a target, not
   * a column of em-dashes.
   *
   * "There is no goal" and "you missed the goal by nothing" are different
   * facts, and a dash is the reader's job to interpret. UX §7.8 asks for the
   * column to go; this is that.
   */
  const hasAnyTarget = months.some((row) => row.target !== null);

  return (
    <section className="space-y-4 border-t border-border pt-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Money in and out</h2>
          <p className="max-w-xl text-sm text-muted-foreground">
            {basis === 'accrual' ? 'What you earned and what you spent. ' : 'What you were paid and what you spent. '}
            {describeBasis(basis)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <BasisToggle value={basis} onChange={setBasis} />
          <PeriodPicker value={period} onChange={setPeriod} id="money-period" />
        </div>
      </div>

      {overview.isError ? (
        <Card>
          <ErrorState error={overview.error} onRetry={() => { void overview.refetch(); }} />
        </Card>
      ) : null}

      {overview.isPending || breakdown.isPending ? <SkeletonTable rows={4} /> : null}

      {overview.data && totals ? (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="Money in" value={<Money value={totals.income} currency={currency} />} caption={period.label} />
            <Tile label="Money out" value={<Money value={totals.expense} currency={currency} />} caption={period.label} />
            <Tile label="Profit" value={<Signed value={totals.profit} currency={currency} />} caption={period.label} />
            {totals.variance && totals.target ? (
              <Tile
                label="vs goal (money in)"
                value={<Signed value={totals.variance} currency={currency} />}
                caption={
                  <>
                    goal <Money value={totals.target} currency={currency} />
                    {totals.targetMonths > 1 ? ` over ${totals.targetMonths} months` : null}
                  </>
                }
              />
            ) : (
              <Tile
                label="vs goal"
                value={<span className="text-base font-normal text-muted-foreground">No target set</span>}
                caption={
                  <Link to="/settings/goals" className="underline" data-tap-exempt="inline text link in a caption">
                    Set a monthly target
                  </Link>
                }
              />
            )}
          </div>

          <Disclosures breakdown={breakdown.data} currency={currency} periodLabel={period.label} />

          {/*
            `grid-cols-1` written out. A grid with no column template lays
            its one implicit column out at min-content, so the card was as
            wide as its widest row wanted to be and ran 6px past a 320px
            screen; `minmax(0, 1fr)` is what makes the card the width of
            the screen and the rows fit inside it.
          */}
          {/* Income by item, HSN/SAC, customer or category; spend by
              category, vendor or project - each row a hover card and a
              click through to the list it is made of. */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BreakdownCard side="income" period={period} basis={basis} title="Where the money came from" />
            <BreakdownCard side="expense" period={period} basis={basis} title="Where it went" />
          </div>

          {/* Spend by category across the year's months so far, on its own axis. */}
          <MonthsChart side="expense" period={fy} basis={basis} title={`Where it went, month by month · ${fy.label}`} />

          <Card>
            <CardHeader>
              <CardTitle>Month by month · {fy.label}</CardTitle>
            </CardHeader>
            <CardContent>
              {/* In and out as a pair per month, profit as the line, on one
                  axis - both are money in this book's currency. Money out is
                  hatched, as the Overview draws it. A month opens the invoices
                  (or, when paid, the payments) dated in it. */}
              <StackedBars
                months={months.map((row) => row.month)}
                groups={[
                  { id: 'in', name: 'Money in', short: 'in', series: [{ id: 'income', name: '', colour: org?.colour ?? null, values: months.map((row) => minor(row.income)) }] },
                  { id: 'out', name: 'Money out', short: 'out', hatch: true, series: [{ id: 'expense', name: '', colour: org?.colour ?? null, values: months.map((row) => minor(row.expense)) }] },
                ]}
                line={{ name: 'Profit', colour: 'foreground', values: months.map((row) => minor(row.profit)) }}
                currency={currency}
                title={`Month by month, ${fy.label}`}
                summary={`Money in and out by month for ${fy.label} so far, with the profit as a line`}
                empty={{ title: `Nothing moved in ${fy.label} yet`, description: 'An invoice, an expense or a deposit dated in this year will show here.' }}
                extraRows={(i) => {
                  const row = months[i];
                  if (!row) return [];
                  return [
                    { key: 'cumulative', label: 'Profit, running', value: moneySigned(row.cumulativeProfit, currency) },
                    ...(row.variance ? [{ key: 'goal', label: 'vs goal', value: moneySigned(row.variance, currency) }] : []),
                    { key: 'click', label: 'Click', value: basis === 'accrual' ? 'opens the invoices' : 'opens the payments' },
                  ];
                }}
                onSelect={(i) => {
                  const row = months[i];
                  if (!row) return;
                  navigate(basis === 'accrual' ? drillForMonth('income', row.month, basis).href : `/payments?where=${encodeURIComponent(JSON.stringify({ conjunction: 'and', conditions: [{ field: 'date', operator: 'between', value: [`${row.month}-01`, lastDayOf(row.month)] }] }))}`);
                }}
                height={220}
              />
            </CardContent>
            <CardContent className="pt-0">
              {isPhone && !tableOpen ? (
                <button
                  type="button"
                  className="inline-flex min-h-11 items-center text-xs text-primary hover:underline"
                  onClick={() => setTableOpen(true)}
                  data-testid="months-table-open"
                >
                  Show the table
                </button>
              ) : null}
              {!isPhone || tableOpen ? (
              <div className="overflow-x-auto" data-testid="months-table">
              <Table>
                <THead>
                  <TR>
                    <TH>Month</TH>
                    <TH className="text-right">Money in</TH>
                    <TH className="text-right">Money out</TH>
                    {/* Only when a foreign-currency payment landed in the
                        year: the difference between the rate an invoice was
                        booked at and the rate the bank credited. A row of
                        zeros would be a question nobody asked. */}
                    {overview.data.exchange ? <TH className="text-right">Exchange gain/(loss)</TH> : null}
                    <TH className="text-right">Profit</TH>
                    {/* NOT "Running balance". It is the running total of
                        profit, and the word "balance" in an accounting product
                        means cash - which this is not, and which would disagree
                        with the cash tile on the same screen. */}
                    <TH className="text-right">Cumulative profit</TH>
                    {hasAnyTarget ? <TH className="text-right">vs goal</TH> : null}
                  </TR>
                </THead>
                <TBody>
                  {months.map((row) => {
                    const picked = pickedMonths.has(row.month);
                    return (
                      <TR
                        key={row.month}
                        // A border marker AND aria-current, not tint alone -
                        // the tint is invisible to a screen reader and nearly
                        // so on a low-contrast display.
                        aria-current={picked ? 'true' : undefined}
                        className={picked ? 'border-l-2 border-l-primary bg-primary/5' : undefined}
                      >
                        <TD>{monthLabel(row.month)}</TD>
                        <TD className="text-right tabular-nums">
                          <Money value={row.income} currency={currency} showSymbol={false} />
                        </TD>
                        <TD className="text-right tabular-nums">
                          <Money value={row.expense} currency={currency} showSymbol={false} />
                        </TD>
                        {overview.data.exchange ? (
                          <TD className="text-right tabular-nums" data-testid="month-exchange">
                            <Signed value={row.exchange ?? '0'} currency={currency} />
                          </TD>
                        ) : null}
                        <TD className="text-right tabular-nums">
                          <Signed value={row.profit} currency={currency} />
                        </TD>
                        <TD className="text-right tabular-nums">
                          <Signed value={row.cumulativeProfit} currency={currency} />
                        </TD>
                        {hasAnyTarget ? (
                          <TD className="text-right tabular-nums">
                            {row.variance ? (
                              <Signed value={row.variance} currency={currency} />
                            ) : (
                              <span className="text-muted-foreground">no target</span>
                            )}
                          </TD>
                        ) : null}
                      </TR>
                    );
                  })}
                  <TR className="font-medium">
                    {/* The divisor is printed, because averaging a half-year
                        over twelve months halves every figure and the reader
                        would have no way to know. */}
                    <TD>
                      Average ({overview.data.monthsWithActivity}{' '}
                      {overview.data.monthsWithActivity === 1 ? 'month' : 'months'})
                    </TD>
                    <TD className="text-right tabular-nums">
                      <Money value={overview.data.averages.income} currency={currency} showSymbol={false} />
                    </TD>
                    <TD className="text-right tabular-nums">
                      <Money value={overview.data.averages.expense} currency={currency} showSymbol={false} />
                    </TD>
                    {overview.data.exchange ? (
                      <TD className="text-right tabular-nums text-xs text-muted-foreground">
                        <Signed value={overview.data.exchange} currency={currency} /> in the year
                      </TD>
                    ) : null}
                    <TD className="text-right tabular-nums">
                      <Signed value={overview.data.averages.profit} currency={currency} />
                    </TD>
                    <TD />
                    {hasAnyTarget ? <TD /> : null}
                  </TR>
                </TBody>
              </Table>
              </div>
              ) : null}
            </CardContent>
          </Card>
        </>
      ) : null}
    </section>
  );
}

/**
 * Money that moved and is deliberately outside the figures above.
 *
 * ── IT IS THE PERIOD'S FIGURE, NOT THE YEAR'S ──
 * It used to be fed from the financial-year query while the tiles above it were
 * period-scoped, so "Left out of these figures: X" under an August tile was the
 * whole year's X. The period-correct figure was already on the wire and never
 * read. A disclosure that is wrong is worse than none: its entire job is to
 * prove nothing was hidden.
 *
 * The arithmetic goes through `@fint/money`, not `Number(a) + Number(b)`. That
 * was here, on this line, which is the one number whose job is to be trusted.
 */
function Disclosures({
  breakdown,
  currency,
  periodLabel,
}: {
  breakdown?: BreakdownResponse;
  currency: string;
  periodLabel: string;
}) {
  if (!breakdown) return null;

  const excluded = add(
    parse(breakdown.excluded.income, currency),
    parse(breakdown.excluded.expense, currency),
  );
  const receipts = parse(breakdown.uncategorisedReceipts.amount, currency);
  if (excluded.minor === 0 && receipts.minor === 0) return null;

  return (
    <p className="text-xs text-muted-foreground">
      {excluded.minor !== 0 ? (
        <>
          Left out of these figures for {periodLabel}: <Money money={excluded} currency={currency} /> in
          categories you have marked as not counting towards your totals.{' '}
        </>
      ) : null}
      {receipts.minor !== 0 ? (
        <>
          A further <Money money={receipts} currency={currency} /> arrived across{' '}
          {breakdown.uncategorisedReceipts.count}{' '}
          {breakdown.uncategorisedReceipts.count === 1 ? 'receipt' : 'receipts'} nobody has said anything
          about, so it is not counted.{' '}
          <Link to="/banking" className="underline">
            Categorise them
          </Link>
        </>
      ) : null}
    </p>
  );
}

/** A signed amount for the chart's readout, through the same formatter the tiles use. */
function moneySigned(value: string, currency: string): string {
  const money = parse(value, currency);
  const text = format({ minor: Math.abs(money.minor), currency });
  return `${money.minor < 0 ? '−' : '+'}${text}`;
}

function lastDayOf(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(Date.UTC(y ?? 1970, m ?? 1, 0)).getUTCDate();
  return `${ym}-${String(last).padStart(2, '0')}`;
}
